import { formatDateForLocale, formatDateTimeForLocale, formatPrice } from '@/lib/formatting.utils'
import hash from 'object-hash'
import { get, cloneDeep } from 'lodash'
import { date } from 'quasar'
const { addToDate } = date

export function generateDialogParams(uid, action, responseData) {
  let dialogParams = {
    uid,
    value: {
      messages: responseData.messages.default,
      confirmAction: action
    }
  }
  Object.keys(responseData.result).forEach(key => {
    dialogParams.value[key] = responseData.result[key]
  })
  return dialogParams
}
/**
 * Prepares options for q-select and any other components using [{label, value}] options; keys for resulting
 * object are configurable; provides sensible defaults - in most cases providing the collection will suffice
 * @param {Object} parameters - a configuration object containing options for
 * processing and sorting
 * @param {Array} parameters.collection - data to generate options from
 * @param {Boolean} [parameters.valueAsObject = false]  - the value for the option should be the whole object
 * @param {String} [parameters.labelKey = name] -  property on the object where we take the label text from
 * @param {String} [parameters.combineLabelWith = null] -  property on the object where we take a value to
 * append to the label
 * @param {String} [parameters.concatSep = space] -  separator of concat string for label
 * @param {String} [parameters.valueKey = id] - property on the object that will serve as the selection value
 * @param {String} [parameters.resultingLabelKey = label] - the key the display value will have
 * @param {String} [parameters.resultingValueKey = value] - the key the value will have
 * @param {Object|String|Number} [parameters.excludeValue = null] - the type of this should match the value we
 * want to exclude and contain sufficient information to isolate the item in the collection; it excludes based
 * on equality with `valueKey`
 * @param {Boolean} [parameters.includeNone = false] - if true, it will add a {value: null, label: 'none'} item
 * to options list
 * @param {Boolean} [parameters.capitalizeOptionLabel = false] - if true, it will capitalize the label string
 * @param {Boolean} [parameters.sort = true] - if true, it will sort the options list after processing,
 * based on provided parameters
 * @param {Boolean} [parameters.sortBefore = false] - whether the collection should be sorted before
 * processing; useful when we want to sort by other values than name / label; it will use the sort params passed in
 * @param {String} [parameters.sortingOptions.sortOrder = asc] - 'asc', 'desc' sort direction
 * @param {String} [parameters.sortingOptions.sortBy = label] - property to sort by
 * @param {String} [parameters.sortingOptions.sortDataType = string] - string, date, number
 * @returns {Array}
 */
export function generateOptions(parameters) {
  let {
    collection,
    valueAsObject = false,
    labelKey = 'name',
    combineLabelWith = null,
    concatSep = ' - ',
    valueKey = 'id',
    resultingLabelKey = 'label',
    resultingValueKey = 'value',
    excludeValue,
    includeNone = false,
    noneLabel = 'None',
    capitalizeOptionLabel = false,
    additionalProperties = [],
    sort = true,
    sortBefore = false,
    sortingOptions = {},
    customLabelDisplay = null
  } = parameters

  let options = []

  if (!collection) {
    return []
  }
  collection = cloneDeep(collection)
  if (sortBefore) collection = sortCollection(collection, sortingOptions)

  if (includeNone) {
    let option = {}
    option[resultingLabelKey] = capitalizeOptionLabel ? capitalize(noneLabel) : noneLabel
    option[resultingValueKey] = null
    options.push(option)
  }
  collection.map(type => {
    if (excludeValue && excludeValueFromCollection(type, valueAsObject, valueKey, excludeValue)) {
      return
    }
    let option = {}
    let label = labelKey ? type[labelKey] : type
    let concatWith = get(type, combineLabelWith, null)
    label = combineLabelWith && concatWith ? label.concat(concatSep, concatWith) : label
    option[resultingLabelKey] = capitalizeOptionLabel ? capitalize(label) : label
    option[resultingValueKey] = valueAsObject || !valueKey ? type : type[valueKey]
    if (customLabelDisplay) {
      option[resultingLabelKey] = customLabelDisplay(label)
    }
    if (additionalProperties.length) {
      additionalProperties.forEach(prop => {
        option[prop] = type[prop]
      })
    }
    options.push(option)
  })

  if (sort) options = sortCollection(options, sortingOptions)

  return options
}
/**
 * Sorts an array based on provided parameters
 * @param {Array} collection - the collection to be sorted
 * @param sortingOptions
 * @param {String} [sortingOptions.sortOrder = asc] - 'asc', 'desc' sort direction
 * @param {String} [sortingOptions.sortBy = label] - property to sort by
 * @param {String} [sortingOptions.sortDataType = string] - string, date, number
 * @returns {*}
 */
export function sortCollection(collection, sortingOptions) {
  let { sortDataType = 'string', sortBy = 'label', sortOrder = 'asc' } = sortingOptions || {
    sortDataType: 'string',
    sortBy: 'label',
    sortOrder: 'asc'
  }
  if (!collection || collection.length === 0) return []
  let sortAsc
  let sortDesc
  switch (sortDataType) {
    case 'number':
      sortAsc = (a, b, by = sortBy) => (sortBy ? a[by] - b[by] : a - b)
      sortDesc = (a, b, by = sortBy) => (by ? b[by] - a[by] : b - a)
      break
    case 'string':
      sortAsc = (atext, btext, by = sortBy) => {
        let a = by ? atext[by].toUpperCase() : atext.toUpperCase()
        let b = by ? btext[by].toUpperCase() : btext.toUpperCase()
        return a < b ? -1 : a > b ? 1 : 0
      }
      sortDesc = (atext, btext, by = sortBy) => {
        let a = by ? atext[by].toUpperCase() : atext.toUpperCase()
        let b = by ? btext[by].toUpperCase() : btext.toUpperCase()
        return a > b ? -1 : a < b
      }
      break
  }
  return collection.sort(sortOrder === 'desc' ? sortDesc : sortAsc)
}
/**
 * Checks if a value should be excluded from the result of generateOptions(params)
 * @param {Object|String|Number} value - value to check for exclusion
 * @param {Boolean} valueAsObject - if true, the value will be treated as an object
 * @param {String} valueKey - if valueAsObject === false, value[valueKey] will be checked for exclusion
 * @param {Object|String|Number} valueToExclude - value to match against for exclusion
 * @returns {boolean} - representing the equality between value and valueToExclude - if true, the value should
 * be skipped
 */
export function excludeValueFromCollection(value, valueAsObject, valueKey, valueToExclude) {
  let valueHash = valueAsObject ? hash(value) : hash(value[valueKey])
  let exclusionHash = hash(valueToExclude)
  return valueHash === exclusionHash
}
/**
 * Capitalized the first letter in every word of the provided text
 * @param text
 * @returns {string}
 */
export function capitalize(text) {
  return text
    .toLowerCase()
    .split(' ')
    .map(s => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ')
}

/**
 * @param a
 * @param b
 * @returns {number}
 */
export function numericSorter(a, b) {
  return a - b
}

/**
 * @param a
 * @param b
 * @param nodeA
 * @param nodeB
 * @returns {number}
 */
export function dateSorter(a, b, nodeA, nodeB) {
  let ADate = new Date(getProp(nodeA, 'data.start_date'))
  let A = ADate ? ADate.getTime() : 0
  let BDate = new Date(getProp(nodeB, 'data.start_date'))
  let B = BDate ? BDate.getTime() : 0
  return B - A
}
/**
 * @param a
 * @param b
 * @param nodeA
 * @param nodeB
 * @returns {number}
 */
export function createdAtSorter(a, b, nodeA, nodeB) {
  let ADate = new Date(getProp(nodeA, 'data.created_at'))
  let A = ADate ? ADate.getTime() : 0
  let BDate = new Date(getProp(nodeB, 'data.created_at'))
  let B = BDate ? BDate.getTime() : 0
  return B - A
}
/**
 * @param a
 * @param b
 * @param nodeA
 * @param nodeB
 * @returns {number}
 */
export function updatedAtSorter(a, b, nodeA, nodeB) {
  let ADate = new Date(getProp(nodeA.data, 'updated_at'))
  let A = ADate ? ADate.getTime() : 0
  let BDate = new Date(getProp(nodeB.data, 'updated_at'))
  let B = BDate ? BDate.getTime() : 0
  return B - A
}
/**
 * @param a
 * @param b
 * @returns {number}
 */
export function entityNameSorter(a, b) {
  let A = get(a, 'name', '')
  let B = get(b, 'name', '')
  return caseInsensitiveValueSorter(A, B)
}
/**
 * @param a
 * @param b
 * @returns {number}
 */
export function caseInsensitiveValueSorter(a, b) {
  let valueA = a ? a.toLowerCase() : ''
  let valueB = b ? b.toLowerCase() : ''
  return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
}
/**
 * @param a
 * @param b
 * @param nodeA
 * @param nodeB
 * @returns {number}
 */
export function personNameSorter(a, b, nodeA, nodeB) {
  let nameA = personRenderer(nodeA.data)
  let nameB = personRenderer(nodeB.data)
  return nameA < nameB ? -1 : nameA > nameB ? 1 : 0
}

/**
 * @param obj
 * @param prop
 * @param defaultValue
 * @returns {*}
 */
export function getProp(obj, prop, defaultValue = 'N/A') {
  let val = get(obj, prop, defaultValue)
  return val || defaultValue
}

/**
 * @param obj
 * @returns {string}
 */
export function periodRenderer(obj) {
  let date = formatDateForLocale(get(obj, 'start_date', null))
  let oneTimePurchase = get(obj, 'one_time_purchase', false)
  return oneTimePurchase ? `One-time purchase: ${date}` : `${date} - ${formatDateForLocale(get(obj, 'end_date', null))}`
}

/**
 * @param date
 * @param data
 * @returns {string}
 */
export function dateRenderer(date, data = null) {
  let d = date ? `${formatDateForLocale(date)}` : 'N/A'
  return getProp(data, 'one_time_purchase', false) ? 'One-time purchase' : `${d}`
}

/**
 *
 * @param date
 * @returns {string}
 */
export function dateTimeRenderer(date) {
  return date ? `${formatDateTimeForLocale(date)}` : 'N/A'
}
/**
 * @param params
 * @param currencyField
 * @param inTooltip
 * @returns {string}
 */
export function priceWithCurrencyRenderer(params, currencyField, inTooltip) {
  let currency = get(params.data, currencyField, '')
  let price = formatPrice(params.value)
  return inTooltip ? (price ? `${price} ${currency}` : 'N/A') : price ? `${price} <small>${currency}</small>` : 'N/A'
}

/**
 * @param price
 * @param decimals
 * @returns {string}
 */
export function priceRenderer(price, decimals = 2) {
  return price ? `${formatPrice(price, decimals)}` : 'N/A'
}

export function personRenderer(obj) {
  let firstName = get(obj, 'first_name')
  let lastName = get(obj, 'last_name')
  let str = firstName || ''
  if (firstName && lastName) {
    str += ' '
  }
  str += lastName || ''
  return str
}

/**
 *
 * @param value
 * @returns {string}
 */
export function percentageRenderer(value) {
  return typeof value !== 'undefined' && value !== null ? `${value}%` : 'N/A'
}

/**
 *
 * @param num
 * @param itemSingular
 * @param itemPlural
 * @returns {string}
 */
export function associatedItemsRenderer(num, itemSingular = 'item', itemPlural = 'items') {
  return num > 1 || num === 0 ? `${num} ${itemPlural}` : `${num} ${itemSingular}`
}

/**
 *
 * @param date
 * @returns {string}
 */
export function getMinDate(date) {
  return addToDate(new Date(date), { days: 1 })
}

/**
 *
 * @param params - ag-grid row params
 * @returns {string}
 */
export function metaValuesRenderer({ value }) {
  const booleanToText = bool => (bool ? 'Yes' : 'No')

  if ([true, false].includes(value)) return booleanToText(value)
  if (!value || !value.length) return 'N/A'
  if (Array.isArray(value)) return value.join(',')
  return value

  // OLD logic (don't delete until making sure the new one works fine)
  // let values = params.value
  // let value
  //
  // if (!values || values.length === 0) {
  //   value = 'N/A'
  // } else {
  //   if (values.length === 1 && typeof values[0] !== 'object') {
  //     if (typeof values[0] === 'boolean') {
  //       value = values[0] ? 'Yes' : 'No'
  //     } else if (!values[0]) value = 'N/A'
  //     else value = values[0]
  //   } else {
  //     value = values.reduce((acc, curr) => {
  //       if (typeof curr === 'object') acc += curr.value + ','
  //       else acc += curr + ','
  //       return acc
  //     }, '').slice(0, -1)
  //   }
  // }
  // return value
}

/**
 * @param metaDataColumns
 * @param columnDefs
 */
export function generateMetaColumns(metaDataColumns, columnDefs) {
  metaDataColumns.forEach(columnData => {
    columnDefs.push({
      headerName: columnData.name,
      field: `meta_data.${columnData.system_name}`,
      cellRenderer: params => {
        params.value = params.data['meta_data'][columnData.system_name]
        return metaValuesRenderer(params)
      },
      tooltipValueGetter: params => {
        if (!params.data) return
        params.value = params.data['meta_data'][columnData.system_name]
        return metaValuesRenderer(params)
      },
      hide: true,
      filterParams: {
        valueGetter: params => {
          params.value = params.data['meta_data'][columnData.system_name]
          return metaValuesRenderer(params)
        }
      },
      valueGetter: params => {
        if (!params.data) return
        params.value = params.data['meta_data'][columnData.system_name]
        return metaValuesRenderer(params)
      }
    })
  })
}

export function placeCaretAtEnd(el) {
  el.focus()
  if (typeof window.getSelection !== 'undefined' && typeof document.createRange !== 'undefined') {
    let range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)
    let sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(range)
  } else if (typeof document.body.createTextRange !== 'undefined') {
    let textRange = document.body.createTextRange()
    textRange.moveToElementText(el)
    textRange.collapse(false)
    textRange.select()
  }
}

export function downloadFile(response, fileName) {
  const fileNameFromBE = response.headers['content-disposition'].split('filename=')[1].replace(/["']/g, '')
  const type = response.headers['content-type']
  const blob = new Blob([response.data], { type, encoding: 'UTF-8' })
  const link = document.createElement('a')
  link.href = window.URL.createObjectURL(blob)
  link.download = fileName || fileNameFromBE
  link.click()
}

export function numberBetween(min, max) {
  if (min === max) throw new Error('min and max must be different, Function: numberBetween')
  if (min > max) [min, max] = [max, min]
  let nr = (max - min) / 2
  if (max - min > 1) nr = Math.floor(nr)
  return nr + min
}

export async function agGridUpdateWithoutLosingEditState(updateFn, ref) {
  const gridOptions = this.$refs[ref].getGridOptions()
  const editingCells = gridOptions.api.getEditingCells()
  if (!editingCells.length) {
    // update happened but we are not editing any cell
    updateFn()
    return
  }
  const {
    rowIndex,
    column: { colId }
  } = editingCells[0]
  gridOptions.api.stopEditing(true)
  updateFn()
  await this.$nextTick()
  gridOptions.api.startEditingCell({
    rowIndex,
    colKey: colId
  })
}

export const isNumber = nr => !isNaN(parseFloat(nr)) && isFinite(nr)

export function getColPropsForImport(field, additionalProps = {}) {
  return {
    field,
    cellClassRules: {
      invalid: params => params.data.errors && params.data.errors[params.colDef.field]
    },
    editable: true,
    cellRenderer: params => params.value || 'N/A',
    tooltipValueGetter: this.generateTooltip,
    ...additionalProps
  }
}
